import { Color } from '@cadstrom/renderer';
import { PcbLayerType } from '@cadstrom/sigma-engine-shared';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { DEFAULT_MODEL_GRID } from '../../constants.js';
import { useActiveDesignContext } from '../../context/active-design-context.js';
import { useDrawingManagerContext } from '../../context/drawing-manager-context.js';
import { GridSettings } from '../../grid-settings/grid-settings.js';
import { useCadRendererLayerSync } from '../../sync/cad-renderer-sync-context.js';
import { LayersColorPicker } from './layers-color-picker.js';

import type { GraphicRenderOpts, GraphicRenderStateOpts } from '@cadstrom/renderer';
import type { Layer, LayerType } from '@cadstrom/sigma-engine-shared';
import type { ReactNode } from 'react';
import type { LayersColorPickerOpts } from './layers-color-picker.js';

export type LayerState = {
	layer: Layer;
	visible: boolean;
};

export type LayersViewProps = {
	anchorEl: HTMLElement;
	onClose?: () => void;
	layerStatesChanged?: (states: LayerState[]) => void;
};

export function LayersView(props: LayersViewProps) {
	const theme = useTheme();
	const { drawingManager } = useDrawingManagerContext();
	const { activeDesign } = useActiveDesignContext();
	const { layerState: syncedLayerState, setLayerVisibility } = useCadRendererLayerSync(activeDesign.layerSet);
	const [lastAltClickLayer, setLastAltClickLayer] = useState<number>();
	const [layerStates, _setLayerStates] = useState<LayerState[]>([]);
	const [previousStates, setPreviousStates] = useState<LayerState[]>([]);
	const [currentPreset, setCurrentPreset] = useState<Preset>(Preset.Custom);
	const [colorPickerLayer, setColorPickerLayer] = useState<LayersColorPickerOpts>();

	const setLayerStates = useCallback(
		(newState: LayerState[] | ((current: LayerState[]) => LayerState[])) => {
			_setLayerStates((current) => {
				setPreviousStates(current);
				const applyState: LayerState[] = typeof newState === 'function' ? newState(current) : newState;
				setLayerVisibility(applyState.filter((l) => l.visible).map((l) => l.layer.id));
				if (props.layerStatesChanged) {
					props.layerStatesChanged(applyState);
				}
				return applyState;
			});
		},
		[props, setLayerVisibility],
	);

	useEffect(() => {
		if (!drawingManager) {
			return;
		}

		_setLayerStates((current) => {
			const syncedVisibleLayers = syncedLayerState ? new Set(syncedLayerState.visibleLayerIds) : undefined;
			const newValues: LayerState[] = [];
			for (const layer of drawingManager.getLayersInUse()) {
				const existing = current.find((l) => l.layer.id === layer.id);
				if (existing) {
					newValues.push({
						layer,
						visible: syncedVisibleLayers?.has(layer.id) ?? existing.visible,
					});
				} else {
					newValues.push({
						layer,
						visible: syncedVisibleLayers?.has(layer.id) ?? !!drawingManager.getLayerIsVisible(layer.id),
					});
				}
			}

			return newValues;
		});
	}, [drawingManager, syncedLayerState]);

	function resetVisibilities() {
		setLayerStates((current) => {
			return current.map((l) => {
				return {
					...l,
					visible: typeof l.layer.defaultVisible === 'boolean' ? l.layer.defaultVisible : true,
				};
			});
		});
		setCurrentPreset(Preset.Custom);
		setLastAltClickLayer(undefined);
	}

	function getLayerColor(layer: Layer, colorName: keyof GraphicRenderStateOpts): React.CSSProperties | undefined {
		const colorOpts = drawingManager?.colorProvider.getColorForLayer(layer.id);
		if (colorOpts) {
			const color = colorOpts[colorName] as GraphicRenderOpts;
			const parsedColor = Color.default(color.tint).alpha(color.alpha);
			return {
				backgroundColor: parsedColor.toString(),
				color: `contrast-color(${parsedColor.toString()})`,
			};
		}

		return;
	}

	function getLayerFaceText(layer: Layer): ReactNode {
		switch (layer.face) {
			case 'B': {
				if (layer.name.startsWith('B.')) {
					return 'Back';
				} else {
					return 'Bottom';
				}
			}

			case 'F': {
				if (layer.name.startsWith('F.')) {
					return 'Front';
				} else {
					return 'Top';
				}
			}

			default: {
				return '';
			}
		}
	}

	function toggleLayerVisibility(e: React.MouseEvent, layer: Layer, visible: boolean): void {
		const newVis = !visible;

		if (e.altKey) {
			if (typeof lastAltClickLayer === 'number' && lastAltClickLayer === layer.id) {
				setLayerStates(previousStates);
			} else {
				setLayerStates((current) => [
					...current.map((l) => ({ ...l, visible: l.layer.id === layer.id ? !newVis : newVis })),
				]);
			}
			setLastAltClickLayer(layer.id);
		} else {
			setLayerStates((current) => [...current.map((l) => (l.layer.id === layer.id ? { ...l, visible: newVis } : l))]);
			setLastAltClickLayer(undefined);
		}

		setCurrentPreset(Preset.Custom);
	}

	function applyPreset(preset: Preset) {
		setCurrentPreset(preset);
		switch (preset) {
			case Preset.AllLayers: {
				setLayerStates((current) => [...current.map((l) => ({ ...l, visible: true }))]);
				break;
			}
			case Preset.NoLayers: {
				setLayerStates((current) => [...current.map((l) => ({ ...l, visible: false }))]);
				break;
			}
			case Preset.BackLayers: {
				setLayerStates((current) => [
					...current.map((l) => ({ ...l, visible: l.layer.face === 'B' || l.layer.type === PcbLayerType.Edge })),
				]);
				break;
			}
			case Preset.FrontLayers: {
				setLayerStates((current) => [
					...current.map((l) => ({ ...l, visible: l.layer.face === 'F' || l.layer.type === PcbLayerType.Edge })),
				]);
				break;
			}
			case Preset.InnerLayers: {
				setLayerStates((current) => [
					...current.map((l) => ({
						...l,
						visible: l.layer.type === PcbLayerType.Inner || l.layer.type === PcbLayerType.Edge,
					})),
				]);
				break;
			}
			case Preset.AllCopper: {
				const includeLayers: LayerType[] = [PcbLayerType.Edge, PcbLayerType.B, PcbLayerType.F, PcbLayerType.Inner];
				setLayerStates((current) => [...current.map((l) => ({ ...l, visible: includeLayers.includes(l.layer.type) }))]);
				break;
			}
			case Preset.OtherLayers: {
				setLayerStates((current) => [...current.map((l) => ({ ...l, visible: l.layer.type === PcbLayerType.Other }))]);
				break;
			}
		}
	}

	function handleOpenColorPicker(e: React.MouseEvent<HTMLDivElement>, layer: Layer) {
		if (colorPickerLayer?.layer === layer) {
			setColorPickerLayer(undefined);
		} else {
			setColorPickerLayer({ layer, anchorEl: e.currentTarget });
		}
		e.stopPropagation();
	}

	let anchor = props.anchorEl;
	if (props.anchorEl.parentElement?.parentElement) {
		anchor = props.anchorEl.parentElement.parentElement;
	} else if (props.anchorEl.parentElement) {
		anchor = props.anchorEl.parentElement;
	}

	const modelType = activeDesign.modelType;

	const el = useDataElTagGenerator(LayersView);
	return (
		<Popover
			slotProps={{ paper: el() }}
			classes={{ paper: 'flex flex-col gap-2 py-2' }}
			open
			anchorEl={anchor}
			onClose={() => props.onClose?.()}
		>
			{colorPickerLayer && (
				<LayersColorPicker open {...colorPickerLayer} onPickComplete={() => setColorPickerLayer(undefined)} />
			)}
			<p className='text-center text-xl font-semibold'>Layer settings</p>

			<Table {...el('table')} className='min-h-0 grow overflow-auto border-t border-gray-a4'>
				<TableBody>
					{layerStates.map(({ layer, visible }) => (
						<TableRow
							key={layer.id}
							className={clsx('cursor-pointer', !visible && 'text-gray-a11 [&_td]:bg-gray-6/30')}
							onClick={(e) => toggleLayerVisibility(e, layer, visible)}
						>
							<TableCell className='py-0.5 pr-0'>
								<div
									className='flex rounded-xs border border-gray-8 px-0.75'
									style={getLayerColor(layer, visible ? 'selectionNeutral' : 'unselected')}
									onClick={(e) => handleOpenColorPicker(e, layer)}
								>
									{visible ?
										<VisibilityIcon className={visibilityIconClasses} />
									:	<VisibilityOffIcon className={visibilityIconClasses} />}
								</div>
							</TableCell>

							<TableCell>
								<span className='tabular-nums'>{`${layer.id} - `}</span>
								{layer.friendlyName ?? layer.name}
							</TableCell>

							{modelType === 'pcb' && <TableCell>{getLayerFaceText(layer)}</TableCell>}
						</TableRow>
					))}
				</TableBody>
			</Table>

			<div className='mx-2 flex justify-between'>
				<Select<Preset>
					value={currentPreset}
					onChange={(e) => applyPreset(e.target.value as Preset)}
					MenuProps={{
						style: { zIndex: theme.zIndex.tooltip },
					}}
				>
					<MenuItem key={-1} value={Preset.Custom} disabled>
						<span>Apply preset</span>
					</MenuItem>
					{modelType === 'pcb' && [
						<MenuItem key={0} value={Preset.AllCopper}>
							<span>All copper layers</span>
						</MenuItem>,
						<MenuItem key={1} value={Preset.FrontLayers}>
							<span>Front layers</span>
						</MenuItem>,
						<MenuItem key={2} value={Preset.InnerLayers}>
							<span>Inner layers</span>
						</MenuItem>,
						<MenuItem key={3} value={Preset.BackLayers}>
							<span>Back layers</span>
						</MenuItem>,
						<MenuItem key={4} value={Preset.OtherLayers}>
							<span>Other layers</span>
						</MenuItem>,
					]}
					<MenuItem key={5} value={Preset.AllLayers}>
						<span>All layers</span>
					</MenuItem>
					<MenuItem key={6} value={Preset.NoLayers}>
						<span>No layers</span>
					</MenuItem>
				</Select>

				<IconButton onClick={() => resetVisibilities()}>
					<SettingsBackupRestoreIcon />
				</IconButton>
			</div>

			{drawingManager?.isGridSupported && (
				<div {...el('grid')} className='flex flex-col gap-2 border-t border-gray-a4 px-3 pt-2'>
					<p className='text-center text-xl font-semibold'>Grid settings</p>
					<GridSettings modelGrid={activeDesign.grid ?? DEFAULT_MODEL_GRID} />
				</div>
			)}
		</Popover>
	);
}

enum Preset {
	Custom,
	AllLayers,
	NoLayers,
	AllCopper,
	FrontLayers,
	BackLayers,
	InnerLayers,
	OtherLayers,
}

const visibilityIconClasses = clsx('opacity-90');
